import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import DateRangeDropdown from '../lpiReports/DateRangeDropdown';
import './CpiDatasetTab.scss';

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

interface CpiDatasetTabProps {
  getSessionTokensFn: () => Promise<SessionTokens>;
}

interface CpiIndicator {
  cpiIndicatorValueId: string;
  carrierId: string;
  carrierName: string | null;
  cpiType: string | null;
  cpiWtw: number | null;
  periodStart: string;
  periodEnd: string;
  sourceFile: string | null;
  uploadedAt: string | null;
}

const PAGE_SIZE = 50;

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return iso.slice(0, 10);
};

const carrierLabel = (r: CpiIndicator): string => r.carrierName ?? r.carrierId;

const CpiDatasetTab: FC<CpiDatasetTabProps> = ({ getSessionTokensFn }) => {
  const { t } = useTranslation();

  const [records, setRecords] = useState<CpiIndicator[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // Committed dates only update on Apply, so the date picker doesn't fetch mid-selection.
  const [committedStartDate, setCommittedStartDate] = useState<Date | null>(null);
  const [committedEndDate, setCommittedEndDate] = useState<Date | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const { jwtToken, sessionId } = await getSessionTokensFn();
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (committedStartDate) params.set('periodStart', format(committedStartDate, 'yyyy-MM-dd'));
      if (committedEndDate) params.set('periodEnd', format(committedEndDate, 'yyyy-MM-dd'));

      const res = await axios.get<{ indicators: CpiIndicator[]; total: number }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/indicators?${params}`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      setRecords(res.data.indicators ?? []);
      setTotal(res.data.total ?? 0);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to load CPI indicators.';
      setFetchError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, committedStartDate, committedEndDate, getSessionTokensFn]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="cpi-dataset">
      <div className="cpi-dataset__header">
        <div>
          <h1>{t('CPI data')}</h1>
          <p className="cpi-dataset__subtitle">
            {t('Carrier CPI indicators ingested from emission-indicator files.')}
          </p>
        </div>
      </div>

      <div className="cpi-dataset__controls">
        <DateRangeDropdown
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          isLoading={isLoading}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setCommittedStartDate(startDate);
            setCommittedEndDate(endDate);
            setPage(1);
          }}
          disabled={!startDate || !endDate}
        >
          {t('Apply')}
        </button>
      </div>

      <div className="cpi-dataset__card">
        <div className="cpi-dataset__toolbar">
          <div className="cpi-dataset__filters">
            <input
              type="text"
              className="cpi-dataset__search-input"
              placeholder={t('Search by carrier…')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {!isLoading && (
            <span className="cpi-dataset__count">
              {total} {total === 1 ? t('record') : t('records')}
            </span>
          )}
        </div>

        {fetchError && <div className="cpi-dataset__error">{fetchError}</div>}

        {!fetchError && (
          <>
            <div
              className={`cpi-dataset__table-wrap${
                isLoading ? ' cpi-dataset__table-wrap--loading' : ''
              }`}
            >
              <table className="cpi-dataset__table">
                <thead>
                  <tr>
                    <th>{t('Carrier')}</th>
                    <th>{t('Start date')}</th>
                    <th>{t('End date')}</th>
                    <th>{t('Type')}</th>
                    <th>{t('CPI (WTW)')}</th>
                    <th>{t('Source file')}</th>
                    <th>{t('Uploaded at')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr>
                      <td colSpan={7} className="cpi-dataset__cell--center">
                        {t('Loading…')}
                      </td>
                    </tr>
                  )}
                  {!isLoading && records.length === 0 && (
                    <tr>
                      <td colSpan={7} className="cpi-dataset__cell--center">
                        {t('No CPI indicators found for the selected filters.')}
                      </td>
                    </tr>
                  )}
                  {!isLoading &&
                    records.map((r) => (
                      <tr key={r.cpiIndicatorValueId}>
                        <td className="cpi-dataset__cell--id">{carrierLabel(r)}</td>
                        <td>{formatDate(r.periodStart)}</td>
                        <td>{formatDate(r.periodEnd)}</td>
                        <td>{r.cpiType ?? '—'}</td>
                        <td>{r.cpiWtw != null ? r.cpiWtw.toLocaleString() : '—'}</td>
                        <td>
                          <span className="cpi-dataset__file" title={r.sourceFile ?? ''}>
                            {r.sourceFile ?? '—'}
                          </span>
                        </td>
                        <td>{formatDate(r.uploadedAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="cpi-dataset__pagination">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(1)}
                  disabled={page === 1 || isLoading}
                  aria-label={t("First page")}
                >
                  «
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1 || isLoading}
                  aria-label={t("Previous page")}
                >
                  ‹
                </button>
                <span className="cpi-dataset__page-info">
                  {t('Page')} {page} {t('of')} {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === totalPages || isLoading}
                  aria-label={t("Next page")}
                >
                  ›
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages || isLoading}
                  aria-label={t("Last page")}
                >
                  »
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CpiDatasetTab;
